import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
} from 'react';
import 'react-credit-cards/es/styles-compiled.css';
import { RouterHistory } from 'react-router';
import { withStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Card from 'react-credit-cards';
import Swal from 'sweetalert2';
import { AppUser } from '../../shared/authorization';
import { alphaNumericOnly } from '../../utility';
import { messages } from '../../agent-panel/sessions/constants';
import SpinLoader from '../../util/SpinLoader';
import useRequest from '../../util/RequestEsign';
import normalizeBrandName from '../../util/normalizeBrandName';

type PaymentMethodSectionProps = {
  classes: Object,
  history: RouterHistory,
  user: AppUser,
  createSnackbar: Function,
  onPaymentStateChange?: ({ hasCard: boolean, hasBank: boolean }) => void,
};

const PRIMARY = '#0f62bd';
const PRIMARY_DARK = '#0a4a91';
const TEXT_MAIN = '#1a1f36';
const TEXT_SECONDARY = '#4a5568';
const BORDER = '#e2e8f0';

const styles = theme => ({
  card: {
    backgroundColor: '#fff',
    borderRadius: 14,
    border: `1px solid ${BORDER}`,
    boxShadow: '0 2px 16px rgba(15,98,189,0.06)',
    marginBottom: 20,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '20px 28px',
    borderBottom: `1px solid ${BORDER}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: TEXT_MAIN,
    margin: 0,
  },
  cardBody: {
    padding: '24px 28px',
  },
  loaderWrap: {
    padding: '32px 0',
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    paddingTop: 4,
    '& > *:not(:first-child)': {
      marginLeft: 12,
    },
  },
  btnPrimary: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    borderRadius: 8,
    padding: '9px 20px',
    '&:hover': { backgroundColor: PRIMARY_DARK },
  },
  btnOutlined: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: TEXT_MAIN,
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    padding: '9px 20px',
    '&:hover': { borderColor: PRIMARY, color: PRIMARY },
  },
  btnDanger: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#dc2626',
    border: '1px solid #fca5a5',
    borderRadius: 8,
    padding: '9px 20px',
    '&:hover': { backgroundColor: '#fef2f2', borderColor: '#dc2626' },
  },
  paymentRemoveRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: 20,
    gap: 10,
  },
  cardWrap: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    '& > *:not(:first-child)': {
      marginLeft: 24,
      [theme.breakpoints.down('xs')]: { marginLeft: 0, marginTop: 16 },
    },
  },
  methodIntro: {
    fontSize: '0.88rem',
    color: TEXT_SECONDARY,
    marginBottom: 14,
    lineHeight: 1.5,
    maxWidth: '100%',
  },
  methodColumns: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    columnGap: 14,
    rowGap: 14,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'stretch',
    },
  },
  methodColumn: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: '1 1 280px',
    maxWidth: 'calc(50% - 7px)',
    boxSizing: 'border-box',
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    padding: '12px 14px 14px',
    backgroundColor: '#fff',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      flex: '1 1 100%',
    },
  },
  methodColumnHeader: {
    marginBottom: 6,
  },
  optionBadge: {
    display: 'inline-block',
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: PRIMARY,
    backgroundColor: '#eef3fb',
    padding: '2px 7px',
    borderRadius: 4,
    marginBottom: 4,
  },
  methodColumnTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: TEXT_MAIN,
    margin: 0,
    lineHeight: 1.25,
  },
  methodColumnDesc: {
    fontSize: '0.8rem',
    color: TEXT_SECONDARY,
    lineHeight: 1.45,
    margin: '4px 0 0',
  },
  bankOnFileWrap: {
    marginTop: 8,
  },
  methodColumnOnFileLabel: {
    fontSize: '0.82rem',
    fontWeight: 700,
    color: TEXT_MAIN,
    marginBottom: 6,
  },
  bankOnFileTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.78rem',
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
    overflow: 'hidden',
    '& tbody tr:last-child td': {
      borderBottom: 'none',
    },
  },
  bankOnFileCellLabel: {
    color: TEXT_SECONDARY,
    fontWeight: 600,
    padding: '6px 10px',
    textAlign: 'left',
    width: '44%',
    backgroundColor: '#f1f5f9',
    borderBottom: `1px solid ${BORDER}`,
    verticalAlign: 'middle',
  },
  bankOnFileCellValue: {
    color: TEXT_MAIN,
    padding: '6px 10px',
    borderBottom: `1px solid ${BORDER}`,
    verticalAlign: 'middle',
    wordBreak: 'break-word',
  },
  methodColumnBody: {
    flex: '1 1 auto',
    minHeight: 0,
  },
  methodColumnActions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: 8,
    gap: 8,
    '& > *': {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  methodColumnActionsFooter: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: 'auto',
    paddingTop: 12,
    gap: 8,
    '& > *': {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  replaceInlineRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
  },
  removeMethodIconBtn: {
    padding: 6,
    color: '#b91c1c',
    '&:hover': {
      backgroundColor: '#fef2f2',
      color: '#991b1b',
    },
  },
  methodColumnSpacer: {
    marginTop: 8,
  },
  bankFormCompact: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: 6,
    '& > *': {
      flex: '1 1 130px',
      marginRight: 8,
      marginTop: 8,
      minWidth: 96,
    },
  },
  creditCardPreviewWrap: {
    marginTop: 8,
    '& .react-credit-card': {
      transform: 'scale(0.78)',
      transformOrigin: 'top left',
    },
  },
});

const PaymentMethodSection = (props: PaymentMethodSectionProps) => {
  const {
    classes,
    history,
    user,
    createSnackbar,
    onPaymentStateChange,
  } = props;

  const [payment, setPayment] = useState(null);
  const [creditCardLoading, setCreditCardLoading] = useState(false);
  const [bankFormOpen, setBankFormOpen] = useState(false);
  const [cardFormOpen, setCardFormOpen] = useState(false);
  const [bankInst, setBankInst] = useState('');
  const [bankTransit, setBankTransit] = useState('');
  const [bankAcct, setBankAcct] = useState('');
  const [savingBank, setSavingBank] = useState(false);

  const hasCard = !!(payment && payment.isCreditCard && payment.creditCardInformation && payment.creditCardInformation.Number);
  const hasBank = !!(payment && payment.hasBankAccount);
  const hasZumProfile = !!(payment && payment.zumTokenID);

  useEffect(() => {
    if (onPaymentStateChange) {
      onPaymentStateChange({ hasCard, hasBank });
    }
  }, [hasCard, hasBank, onPaymentStateChange]);

  const loadPaymentMethods = useCallback(async () => {
    setCreditCardLoading(true);
    try {
      const cardData = await useRequest({ endPoint: '/card' });
      if (cardData && cardData.message && !cardData.zumTokenID) {
        setPayment(null);
      } else {
        setPayment(cardData);
      }
    } catch (error) {
      createSnackbar(messages.GET_CC_ERROR, { timer: 5000 });
    }
    setCreditCardLoading(false);
  }, [createSnackbar]);

  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  const deletePaymentByScope = (scope, title, html) => {
    const query = scope === 'all' ? '' : `?scope=${encodeURIComponent(scope)}`;
    Swal.fire({
      title,
      html: `<i>${html}</i>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remove',
      cancelButtonText: 'Cancel',
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        await useRequest({
          endPoint: `/card${query}`,
          method: 'DELETE',
          responseFormat: 'raw',
        });
      },
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      Swal.fire({
        icon: 'success',
        title: 'Removed',
        toast: true,
        showConfirmButton: false,
        timer: 1500,
      });
      loadPaymentMethods();
    }).catch(() => {
      createSnackbar(messages.ERROR_DELETE_CARD, { timer: 5000 });
    });
  };

  const saveBankAccount = async () => {
    const inst = bankInst.replace(/\D/g, '').slice(0, 3);
    const transit = bankTransit.replace(/\D/g, '').slice(0, 5);
    const acct = bankAcct.replace(/\s/g, '');
    if (inst.length !== 3 || transit.length !== 5 || acct.length < 4) {
      createSnackbar('Enter a valid institution (3 digits), transit (5 digits), and account number.', { timer: 6000 });
      return;
    }
    setSavingBank(true);
    try {
      const body = {
        BankAccountInformation: {
          InstitutionNumber: inst,
          TransitNumber: transit,
          AccountNumber: acct,
        },
      };
      if (!hasZumProfile) {
        body.firstName = alphaNumericOnly(user.firstName || '');
        body.lastName = alphaNumericOnly(user.lastName || '');
        body.email = user.email || '';
      }
      const res = await useRequest({
        endPoint: '/card',
        method: 'POST',
        body,
        responseFormat: 'raw',
      });
      if (res.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Bank account saved',
          toast: true,
          showConfirmButton: false,
          timer: 1500,
        });
        setBankInst('');
        setBankTransit('');
        setBankAcct('');
        setBankFormOpen(false);
        await loadPaymentMethods();
      } else {
        throw new Error('save failed');
      }
    } catch (error) {
      createSnackbar(messages.GET_CC_ERROR, { timer: 5000 });
    }
    setSavingBank(false);
  };

  const changeCard = () => {
    history.push(`/payment/creditcards?status=${hasCard ? 'replace' : 'new'}`);
  };

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <h2 className={classes.cardTitle}>Payment Method</h2>
      </div>
      <div className={classes.cardBody}>
        {creditCardLoading ? (
          <div className={classes.loaderWrap}><SpinLoader /></div>
        ) : (
          <Fragment>
            <p className={classes.methodIntro}>
              You can add one or both options below. Use a credit card, or link a Canadian bank
              account for EFT (PAD), or both — when subscribing you can choose which to charge.
            </p>

            <div className={classes.methodColumns}>
              <div className={classes.methodColumn}>
                <div className={classes.methodColumnHeader}>
                  <div className={classes.optionBadge}>Option 1</div>
                  <h3 className={classes.methodColumnTitle}>Credit card</h3>
                </div>
                <p className={classes.methodColumnDesc}>
                  Visa, Mastercard, and other major cards. Card details are entered on a secure page.
                </p>
                {hasCard && payment && (
                  <div className={classes.creditCardPreviewWrap}>
                    <div className={classes.cardWrap}>
                      <Card
                        number={payment.creditCardInformation.Number}
                        expiry={`${payment.creditCardInformation.ExpireMonth}/${
                          String(payment.creditCardInformation.ExpireYear || '').slice(-2)
                        }`}
                        cvc={payment.creditCardInformation.CVV || '***'}
                        issuer={normalizeBrandName(
                          payment.creditCardInformation.BrandName || '',
                        )}
                        name={`${payment.creditCardInformation.FirstName} ${payment.creditCardInformation.LastName}`}
                        preview
                      />
                    </div>
                  </div>
                )}
                {!hasCard && !cardFormOpen && (
                  <div className={classes.methodColumnActions}>
                    <Button size="small" variant="outlined" className={classes.btnOutlined} onClick={() => setCardFormOpen(true)}>
                      Add credit card
                    </Button>
                  </div>
                )}
                {!hasCard && cardFormOpen && (
                  <Fragment>
                    <div className={classes.methodColumnSpacer}>
                      <p className={classes.methodColumnDesc}>
                        You will be taken to a secure form to enter your card information.
                      </p>
                    </div>
                    <div className={classes.methodColumnActions}>
                      <Button size="small" variant="contained" className={classes.btnPrimary} onClick={changeCard}>
                        Continue to card form
                      </Button>
                      <Button size="small" variant="outlined" className={classes.btnOutlined} onClick={() => setCardFormOpen(false)}>
                        Cancel
                      </Button>
                    </div>
                  </Fragment>
                )}
                {hasCard && (
                  <div className={classes.methodColumnSpacer}>
                    {!cardFormOpen ? (
                      <div className={`${classes.methodColumnActions} ${classes.replaceInlineRow}`}>
                        <Button size="small" variant="outlined" className={classes.btnOutlined} onClick={() => setCardFormOpen(true)}>
                          Replace card
                        </Button>
                        <IconButton
                          size="small"
                          className={classes.removeMethodIconBtn}
                          aria-label="Remove credit card"
                          title="Remove credit card"
                          onClick={() => deletePaymentByScope(
                            'card',
                            'Remove credit card?',
                            'Remove your saved credit card from this app.',
                          )}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </div>
                    ) : (
                      <Fragment>
                        <p className={classes.methodColumnDesc}>
                          Replace your card on the secure form. Your previous card will be updated after you finish.
                        </p>
                        <div className={classes.methodColumnActions}>
                          <Button size="small" variant="contained" className={classes.btnPrimary} onClick={changeCard}>
                            Continue to card form
                          </Button>
                          <Button size="small" variant="outlined" className={classes.btnOutlined} onClick={() => setCardFormOpen(false)}>
                            Cancel
                          </Button>
                        </div>
                      </Fragment>
                    )}
                  </div>
                )}
              </div>

              <div className={classes.methodColumn}>
                <div className={classes.methodColumnBody}>
                  <div className={classes.methodColumnHeader}>
                    <div className={classes.optionBadge}>Option 2</div>
                    <h3 className={classes.methodColumnTitle}>Bank account (EFT)</h3>
                  </div>
                  <p className={classes.methodColumnDesc}>
                    Canadian PAD — institution number (3 digits), transit (5), and account number.
                    {' '}
                    Information is sent securely to our payment provider.
                  </p>
                  {hasBank && payment && (
                    <div className={classes.bankOnFileWrap}>
                      <div className={classes.methodColumnOnFileLabel}>On file</div>
                      <table className={classes.bankOnFileTable}>
                        <tbody>
                          {payment.bankAccountLast4 && (
                            <tr>
                              <td className={classes.bankOnFileCellLabel}>Account ending in</td>
                              <td className={classes.bankOnFileCellValue}>
                                <strong>{payment.bankAccountLast4}</strong>
                              </td>
                            </tr>
                          )}
                          {!!payment.bankAccountInformation?.InstitutionNumber && (
                            <tr>
                              <td className={classes.bankOnFileCellLabel}>Institution</td>
                              <td className={classes.bankOnFileCellValue}>
                                {payment.bankAccountInformation.InstitutionNumber}
                              </td>
                            </tr>
                          )}
                          {!!payment.bankAccountInformation?.TransitNumber && (
                            <tr>
                              <td className={classes.bankOnFileCellLabel}>Transit</td>
                              <td className={classes.bankOnFileCellValue}>
                                {payment.bankAccountInformation.TransitNumber}
                              </td>
                            </tr>
                          )}
                          {!!payment.bankAccountInformation?.AccountNumber && (
                            <tr>
                              <td className={classes.bankOnFileCellLabel}>Account</td>
                              <td className={classes.bankOnFileCellValue}>
                                {payment.bankAccountInformation.AccountNumber}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {bankFormOpen && (
                    <div className={classes.bankFormCompact}>
                      <TextField
                        label="Institution number"
                        value={bankInst}
                        onChange={(e) => {
                          setBankInst(e.target.value.replace(/\D/g, '').slice(0, 3));
                        }}
                        inputProps={{ maxLength: 3, inputMode: 'numeric' }}
                        variant="outlined"
                        margin="dense"
                      />
                      <TextField
                        label="Transit number"
                        value={bankTransit}
                        onChange={(e) => {
                          setBankTransit(e.target.value.replace(/\D/g, '').slice(0, 5));
                        }}
                        inputProps={{ maxLength: 5, inputMode: 'numeric' }}
                        variant="outlined"
                        margin="dense"
                      />
                      <TextField
                        label="Account number"
                        value={bankAcct}
                        onChange={(e) => {
                          setBankAcct(e.target.value);
                        }}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                    </div>
                  )}
                </div>
                {!bankFormOpen ? (
                  <div className={`${classes.methodColumnActionsFooter} ${classes.replaceInlineRow}`}>
                    <Button
                      size="small"
                      variant="outlined"
                      className={classes.btnOutlined}
                      onClick={() => setBankFormOpen(true)}
                    >
                      {hasBank ? 'Replace bank account' : 'Enter bank account details'}
                    </Button>
                    {hasBank && (
                      <IconButton
                        size="small"
                        className={classes.removeMethodIconBtn}
                        aria-label="Remove bank account"
                        title="Remove bank account"
                        onClick={() => deletePaymentByScope(
                          'bank',
                          'Remove bank account?',
                          'Remove your saved Canadian bank account (EFT) from this app.',
                        )}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                ) : (
                  <div className={classes.methodColumnActionsFooter}>
                    <Button
                      size="small"
                      variant="contained"
                      className={classes.btnPrimary}
                      disabled={savingBank}
                      onClick={saveBankAccount}
                    >
                      Save bank account
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      className={classes.btnOutlined}
                      disabled={savingBank}
                      onClick={() => setBankFormOpen(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {hasCard && hasBank && (
              <div className={classes.paymentRemoveRow}>
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.btnDanger}
                  onClick={() => deletePaymentByScope(
                    'all',
                    'Remove all payment methods?',
                    'Remove both your saved credit card and bank account from this app.',
                  )}
                >
                  Remove all
                </Button>
              </div>
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(PaymentMethodSection);
